<script context="module" lang="ts">
	const THROTTLE_LIMIT = 50;
	const LOOP_CHECK_TIMEOUT = 1000;
	const LOOP_CHECK_MAX_CALLS = 5;

	const throttler = {
		timers: [] as number[],
		caches: [] as Function[],
		throttle(fn: Function) {
			if (this.caches.indexOf(fn) === -1) {
				// cache current handler
				this.caches.push(fn);

				// save timer for current handler
				this.timers.push(
					window.setTimeout(() => {
						fn();
						this.caches.splice(this.caches.indexOf(fn), 1);
						this.timers.shift();
					}, THROTTLE_LIMIT) as number
				);
			}
		},

		reset() {
			// reset all timers
			this.timers.forEach((timer) => {
				clearTimeout(timer);
			});
			this.timers.length = 0;

			// empty caches
			this.caches = [];
		}
	};

	const loopTracker = {
		isChecked: false,
		timer: null as null | number,
		times: 0,
		track() {
			this.times += 1;
			clearTimeout(this.timer ?? undefined);
			this.timer = window.setTimeout(() => {
				this.isChecked = true;
			}, LOOP_CHECK_TIMEOUT);
			if (this.times > LOOP_CHECK_MAX_CALLS) {
				this.isChecked = true;
			}
		}
	};

	const scrollBarStorage = {
		key: "_infiniteScrollHeight",

		getScrollElement(element: HTMLElement | Window) {
			return element === window ? document.documentElement : element;
		},

		save(element: HTMLElement | Window) {
			const target = this.getScrollElement(element) as any;
			target[this.key] = target.scrollHeight;
		},

		restore(element: HTMLElement | Window) {
			const target = this.getScrollElement(element) as any;
			if (typeof target[this.key] === "number") {
				target.scrollTop =
					target.scrollHeight - target[this.key] + target.scrollTop;
			}
			this.remove(target);
		},

		remove(element: HTMLElement | Window) {
			if ((element as any)[this.key] !== undefined) {
				delete (element as any)[this.key];
			}
		}
	};

	function isVisible(element: HTMLElement | Window) {
		return (
			element &&
			!(element instanceof Window) &&
			element.offsetWidth + element.offsetHeight > 0
		);
	}
</script>

<script lang="ts">
	import { BROWSER } from "esm-env";
	import { onMount, onDestroy, tick, createEventDispatcher } from "svelte";
	const dispatch = createEventDispatcher();

	const STATUS = {
		READY: 0,
		LOADING: 1,
		COMPLETE: 2,
		ERROR: 3
	};

	export let distance = 100;
	export let direction = "bottom";
	export let forceUseInfiniteWrapper = false;
	export let identifier = +new Date();
	export let status = STATUS.READY;

	let isFirstLoad = true;
	let mounted = false;
	let thisElement: HTMLElement;
	let scrollParent: HTMLElement | Window;
	let resizeObserver: ResizeObserver;

	$: showSpinner = status === STATUS.LOADING;
	$: showError = status === STATUS.ERROR;
	$: showNoResults = status === STATUS.COMPLETE && isFirstLoad;
	$: showNoMore = status === STATUS.COMPLETE && !isFirstLoad;

	const stateChanger: InfiniteState = {
		loaded: async () => {
			isFirstLoad = false;
			if (direction === "top") {
				await tick();
				scrollBarStorage.restore(scrollParent);
			}
			if (status === STATUS.LOADING) {
				await tick();
				requestAnimationFrame(async () => {
					await attemptLoad(true);
				});
			}
		},

		complete: async () => {
			status = STATUS.COMPLETE;

			// force re-complation computed properties to fix the problem of get slot text delay
			await tick();

			resizeObserver?.disconnect();
			scrollParent.removeEventListener("scroll", scrollHandler);
		},

		reset: () => {
			status = STATUS.READY;
			isFirstLoad = true;

			scrollBarStorage.remove(scrollParent);

			scrollParent.addEventListener("scroll", scrollHandler, { passive: true });

			// wait for list to be empty and the empty action may trigger a scroll event
			setTimeout(() => {
				throttler.reset();
			}, 1);
		},

		error: () => {
			status = STATUS.ERROR;
			throttler.reset();
		},

		get: () => status
	};

	function scrollHandler(event: null | Event = null) {
		if (status === STATUS.READY) {
			if (event && event.constructor === Event && isVisible(thisElement)) {
				throttler.throttle(attemptLoad);
			} else {
				attemptLoad();
			}
		}
	}

	// Attempt to trigger load
	async function attemptLoad(isContinuousCall = false) {
		if (
			status !== STATUS.COMPLETE &&
			isVisible(thisElement) &&
			BROWSER &&
			getCurrentDistance() <= distance
		) {
			status = STATUS.LOADING;

			if (direction === "top") {
				// wait for spinner display
				await tick();
				scrollBarStorage.save(scrollParent);
			}

			if (loopTracker.times >= LOOP_CHECK_MAX_CALLS) {
			} else {
				dispatch("infinite", isFirstLoad);
				isFirstLoad = false;
			}

			if (
				isContinuousCall &&
				!forceUseInfiniteWrapper &&
				!loopTracker.isChecked
			) {
				loopTracker.track();
			}
		} else if (status === STATUS.LOADING) {
			status = STATUS.READY;
		}
	}

	// Get current distance from the specified direction
	function getCurrentDistance() {
		let distance;

		if (direction === "top") {
			distance =
				scrollParent instanceof Window
					? scrollParent.pageYOffset
					: scrollParent.scrollTop;
		} else {
			const infiniteElementOffsetTopFromBottom =
				thisElement.getBoundingClientRect().top;
			const scrollElementOffsetTopFromBottom =
				scrollParent instanceof Window
					? window.innerHeight
					: scrollParent.getBoundingClientRect().bottom;

			distance =
				infiniteElementOffsetTopFromBottom - scrollElementOffsetTopFromBottom;
		}

		return distance;
	}

	// Get the first scroll parent of an element
	function getScrollParent(element = thisElement): HTMLElement | Window {
		let result;

		if (typeof forceUseInfiniteWrapper === "string") {
			result = document.querySelector(forceUseInfiniteWrapper);
		}

		if (!result) {
			if (element.tagName === "BODY") {
				result = window;
			} else if (
				!forceUseInfiniteWrapper &&
				["scroll", "auto"].indexOf(getComputedStyle(element).overflowY) > -1
			) {
				result = element;
			} else if (
				element.hasAttribute("infinite-wrapper") ||
				element.hasAttribute("data-infinite-wrapper")
			) {
				result = element;
			}
		}

		return result || getScrollParent(element.parentNode as HTMLElement);
	}

	function updateScrollParent() {
		if (mounted) {
			scrollParent = getScrollParent();
			if (!(scrollParent instanceof Window)) {
				resizeObserver = new ResizeObserver(async () => {
					if (status !== STATUS.LOADING) {
						await tick();
						await attemptLoad();
					}
				});
				resizeObserver.observe(scrollParent);
			}
		}
	}

	function identifierUpdated() {
		if (mounted && status !== STATUS.COMPLETE) {
			stateChanger.reset();
		}
	}

	// Watch forceUseInfiniteWrapper and mounted
	$: forceUseInfiniteWrapper, mounted, updateScrollParent();

	// Watch identifier and mounted
	$: identifier, mounted, identifierUpdated();

	onMount(async () => {
		mounted = true;
		dispatch("initialize", stateChanger);
		setTimeout(() => {
			scrollParent.addEventListener("scroll", scrollHandler, { passive: true });
		}, 50);
	});

	onDestroy(() => {
		resizeObserver?.disconnect();
		if (mounted && status !== STATUS.COMPLETE) {
			throttler.reset();
			scrollBarStorage.remove(scrollParent);
			scrollParent.removeEventListener("scroll", scrollHandler);
		}
	});
</script>

<div
	class="infinite-loading-container {$$props.class || ''}"
	bind:this="{thisElement}">
	{#if showSpinner}
		<div class="infinite-status-prompt">
			<slot name="spinner" isFirstLoad="{isFirstLoad}">Loading...</slot>
		</div>
	{/if}

	{#if showNoResults}
		<div class="infinite-status-prompt">
			<slot name="noResults" />
		</div>
	{/if}

	{#if showNoMore}
		<div class="infinite-status-prompt">
			<slot name="noMore" />
		</div>
	{/if}

	{#if showError}
		<div class="infinite-status-prompt">
			<slot name="error" attemptLoad="{attemptLoad}">
				Oops, something went wrong :(
				<br />
				<button type="button" on:click="{() => attemptLoad()}"> Retry </button>
			</slot>
		</div>
	{/if}
</div>
